import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { warehouseApi } from '../../services/api';
import type { WarehouseFormData, WarehouseType } from '../../types';

interface WarehouseFormProps {
  mode: 'create' | 'edit';
}

const WarehouseForm: React.FC<WarehouseFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useCoordinates, setUseCoordinates] = useState(false);

  const [formData, setFormData] = useState<WarehouseFormData>({
    code: '',
    name: '',
    warehouse_type: 'branch',
    is_active: true,
    address: '',
    address_number: '',
    address_complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zip_code: '',
    latitude: undefined,
    longitude: undefined,
    phone: '',
    whatsapp: '',
    email: '',
    manager_name: '',
    notes: '',
  });

  useEffect(() => {
    if (mode === 'edit' && id) {
      loadWarehouse();
    }
  }, [mode, id]);

  const loadWarehouse = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const warehouse = await warehouseApi.getWarehouse(id);
      setFormData({
        code: warehouse.code,
        name: warehouse.name,
        warehouse_type: warehouse.warehouse_type,
        is_active: warehouse.is_active,
        address: warehouse.address || '',
        address_number: warehouse.address_number || '',
        address_complement: warehouse.address_complement || '',
        neighborhood: warehouse.neighborhood || '',
        city: warehouse.city || '',
        state: warehouse.state || '',
        zip_code: warehouse.zip_code || '',
        latitude: warehouse.latitude,
        longitude: warehouse.longitude,
        phone: warehouse.phone || '',
        whatsapp: warehouse.whatsapp || '',
        email: warehouse.email || '',
        manager_name: warehouse.manager_name || '',
        notes: warehouse.notes || '',
      });

      // Se tem coordenadas mas não tem endereço, ativar modo GPS
      if (warehouse.latitude && warehouse.longitude && !warehouse.address) {
        setUseCoordinates(true);
      }
    } catch (err) {
      setError('Erro ao carregar depósito');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCoordinatesToggle = (checked: boolean) => {
    setUseCoordinates(checked);
    if (checked) {
      // Limpar campos de endereço
      setFormData(prev => ({
        ...prev,
        address: '',
        address_number: '',
        address_complement: '',
        neighborhood: '',
        city: '',
        state: '',
        zip_code: '',
      }));
    } else {
      // Limpar coordenadas
      setFormData(prev => ({
        ...prev,
        latitude: undefined,
        longitude: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!formData.code || !formData.name) {
      setError('Código e nome são obrigatórios');
      return;
    }

    if (!useCoordinates) {
      if (!formData.address || !formData.city || !formData.state) {
        setError('Endereço completo é obrigatório (ou use coordenadas GPS)');
        return;
      }
    } else {
      if (!formData.latitude || !formData.longitude) {
        setError('Latitude e longitude são obrigatórias no modo GPS');
        return;
      }
    }

    try {
      setLoading(true);
      setError('');

      if (mode === 'create') {
        await warehouseApi.createWarehouse(formData);
      } else if (id) {
        await warehouseApi.updateWarehouse(id, formData);
      }

      navigate('/warehouses');
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail ||
                      Object.values(err.response?.data || {}).flat().join(', ') ||
                      'Erro ao salvar depósito';
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && mode === 'edit') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-base font-bold text-gray-900">
          {mode === 'create' ? 'Novo Depósito' : 'Editar Depósito'}
        </h1>
        <p className="text-gray-600 mt-1">
          {mode === 'create'
            ? 'Cadastre uma nova base/depósito da empresa'
            : 'Edite as informações do depósito'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-lg p-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Informações Básicas */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Informações Básicas</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código *
              </label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: MAT-SJC, FIL-PAU"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Matriz São José dos Campos"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo *
              </label>
              <select
                name="warehouse_type"
                value={formData.warehouse_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="branch">Filial</option>
                <option value="main">Matriz</option>
              </select>
            </div>

            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Depósito ativo</span>
              </label>
            </div>
          </div>
        </div>

        {/* Localização */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Localização</h3>

          <div className="mb-4 bg-purple-50 border border-purple-200 rounded-md p-3">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={useCoordinates}
                onChange={(e) => handleCoordinatesToggle(e.target.checked)}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm font-medium text-purple-900">
                Usar coordenadas GPS (para locais sem endereço)
              </span>
            </label>
          </div>

          {!useCoordinates ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CEP
                </label>
                <input
                  type="text"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="00000-000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endereço *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={!useCoordinates}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número
                </label>
                <input
                  type="text"
                  name="address_number"
                  value={formData.address_number}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Complemento
                </label>
                <input
                  type="text"
                  name="address_complement"
                  value={formData.address_complement}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bairro
                </label>
                <input
                  type="text"
                  name="neighborhood"
                  value={formData.neighborhood}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cidade *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={!useCoordinates}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado *
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="SP"
                  maxLength={2}
                  required={!useCoordinates}
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-green-50 p-4 rounded-md border border-green-200">
              <div>
                <label className="block text-sm font-medium text-green-900 mb-1">
                  Latitude *
                </label>
                <input
                  type="number"
                  step="any"
                  name="latitude"
                  value={formData.latitude || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="-23.5505"
                  required={useCoordinates}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-green-900 mb-1">
                  Longitude *
                </label>
                <input
                  type="number"
                  step="any"
                  name="longitude"
                  value={formData.longitude || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="-46.6333"
                  required={useCoordinates}
                />
              </div>
            </div>
          )}
        </div>

        {/* Contato */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Contato</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefone
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="(12) 3456-7890"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp
              </label>
              <input
                type="text"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="(12) 98765-4321"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="deposito@empresa.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Responsável
              </label>
              <input
                type="text"
                name="manager_name"
                value={formData.manager_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nome do responsável"
              />
            </div>
          </div>
        </div>

        {/* Observações */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Observações</h3>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Horários de funcionamento, restrições, etc..."
          />
        </div>

        {/* Botões */}
        <div className="flex justify-end space-x-2 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate('/warehouses')}
            className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Salvando...' : mode === 'create' ? 'Criar Depósito' : 'Salvar Alterações'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WarehouseForm;
